import React, { useEffect, useState } from "react";
import { API_BASE, mediaUrl } from "../../lib/api";

/**
 * Catalogue photography arrives from the API as a bare URL, so it cannot carry
 * the responsive ladder that assets.js descriptors do — which meant a 390px
 * phone downloaded the same file a desktop did. One of the two source CDNs
 * encodes the rendered width as a path segment, and it honours any value we
 * ask for: the same shot is 88 KB at 2560 and 21 KB at 720. Where the pattern
 * matches, the ladder is reconstructed from the URL.
 */
const CDN_SIZE_PATH = /^(https:\/\/cdn\.myikas\.com\/images\/[^/]+\/[^/]+\/)(\d{2,4})(\/.+)$/;
const CDN_WIDTHS = [360, 540, 720, 1080, 1600, 2560];

/* The catalogue CDN renders the width from the path, but it does not have every
   tier: `/1600/` answers 404 while its neighbours are fine. A 2x display drawing
   an image at ~790px asks for exactly 1600, so the browser picked the one rung
   that does not exist and the picture silently failed to load. Our own thumbnail
   endpoint renders any width in the list, so only this ladder drops it. */
const CDN_PATH_WIDTHS = CDN_WIDTHS.filter((width) => width !== 1600);

/**
 * The other source has no ladder of its own: the ERP backoffice serves the
 * original of every photograph — over a megabyte apiece — and ignores any
 * resize parameter. A grid of forty cards asked for forty megabytes over six
 * connections and the cards below the fold never arrived. The backend renders
 * the ladder for these instead; `src` stays the original so a browser without
 * srcSet support still gets a picture.
 */
const THUMB_HOSTS = ["backoffice.atelierdifalco.com", "backoffice.atelierdifalco.az"];

function isThumbSource(url) {
  const value = String(url || "");
  return THUMB_HOSTS.some((host) => value.startsWith(`https://${host}/`));
}

function thumbSrcSet(url) {
  if (!isThumbSource(url)) return undefined;
  const encoded = encodeURIComponent(String(url));
  return CDN_WIDTHS
    .map((width) => `${API_BASE}/media/thumb?src=${encoded}&w=${width} ${width}w`)
    .join(", ");
}

/**
 * Our own media API serves the uploaded original at full camera resolution —
 * homepage heroes are routinely 2–3 MB — and honours a `?w=` width by rendering
 * (and caching) a resized copy. Public library and studio images are safe to
 * ask for at the size they are actually drawn; private media (Try-On, avatars)
 * is signed and never resized here.
 */
const LOCAL_MEDIA_PATH = /\/api\/media\/(?:library|studio)\//;

function isLocalPublicMedia(url) {
  const value = String(url || "");
  return LOCAL_MEDIA_PATH.test(value) && !/[?&](exp|sig)=/.test(value);
}

function localMediaSrcSet(url) {
  if (!isLocalPublicMedia(url)) return undefined;
  const [base, query] = String(url).split("?");
  const params = new URLSearchParams(query || "");
  return CDN_WIDTHS
    .map((width) => {
      params.set("w", String(width));
      return `${base}?${params.toString()} ${width}w`;
    })
    .join(", ");
}

function catalogueSrcSet(url) {
  const match = CDN_SIZE_PATH.exec(String(url || ""));
  if (!match) return localMediaSrcSet(url) || thumbSrcSet(url);
  const [, prefix, , suffix] = match;
  return CDN_PATH_WIDTHS.map((width) => `${prefix}${width}${suffix} ${width}w`).join(", ");
}

/**
 * Intrinsic dimensions for a catalogue URL.
 *
 * A bare CDN string carries no width or height, so every catalogue image
 * rendered with neither attribute and the browser reserved nothing for it —
 * the row collapsed and then jumped once the bytes arrived. That is the
 * component's stated purpose ("the four things that are easy to forget") and
 * it was the one it did not do.
 *
 * The whole catalogue is shot to a 3:4 portrait ratio, which the CSS already
 * assumes (`.adf-product-card-media`, the PDP panels). Supplying the width from
 * the ladder and the matching height gives the browser an aspect ratio to hold
 * before the image loads. `max-width: 100%` in the CSS keeps it responsive.
 */
const CATALOGUE_RATIO = 4 / 3;   // height / width

function catalogueIntrinsic(url) {
  if (!CDN_SIZE_PATH.test(String(url || "")) && !isThumbSource(url)) return {};
  const width = 720;
  return { width, height: Math.round(width * CATALOGUE_RATIO) };
}

/**
 * The storefront's only <img>.
 *
 * It exists so the four things that are easy to forget are impossible to
 * forget: a width/height pair (so the box is reserved and the page does not
 * shift when the picture lands), a srcSet/sizes pair (so a phone does not
 * download a 1080px file to draw it at 180), lazy loading below the fold, and
 * async decoding.
 *
 * `source` takes either a responsive descriptor from assets.js
 * ({ src, srcSet, width, height }) or a plain URL — catalogue images come from
 * the API as bare strings and cannot carry a ladder.
 *
 * `sizes` describes how wide the picture will actually be drawn, per
 * breakpoint. Get it wrong and the browser picks the wrong tier, so it is
 * required whenever a srcSet is present.
 */
export default function AdfImage({
  source,
  mobileSource = null,
  alt = "",
  sizes,
  className = "",
  priority = false,
  fallback = null,
  width,
  height,
  onError,
  ...rest
}) {
  const descriptor = typeof source === "string" || !source
    ? (() => {
        const src = mediaUrl(source) || undefined;
        return { src, srcSet: catalogueSrcSet(src) };
      })()
    : source;

  const fallbackDescriptor = typeof fallback === "string" || !fallback
    ? (() => {
        const src = mediaUrl(fallback) || undefined;
        return src ? { src, srcSet: catalogueSrcSet(src) } : null;
      })()
    : fallback;

  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    setUseFallback(false);
  }, [descriptor?.src, fallbackDescriptor?.src]);

  const resolved = useFallback && fallbackDescriptor?.src
    ? fallbackDescriptor
    : (descriptor.src ? descriptor : (fallbackDescriptor || descriptor));
  if (!resolved?.src) return null;
  const intrinsic = catalogueIntrinsic(resolved.src);

  /* Art direction, not just resizing: a campaign shot cropped for a wide hero
     is a letterbox on a phone, so an editor can supply a portrait version.
     `<picture>` picks one, which is the point — `srcSet` alone would let the
     browser download whichever it liked, and both files would be in flight. */
  const mobile = mobileSource
    ? (typeof mobileSource === "string"
      ? (() => { const src = mediaUrl(mobileSource) || undefined; return src ? { src, srcSet: catalogueSrcSet(src) } : null; })()
      : mobileSource)
    : null;

  const image = (
    <img
      src={resolved.src}
      srcSet={resolved.srcSet}
      sizes={resolved.srcSet ? sizes : undefined}
      alt={alt}
      width={width ?? resolved.width ?? intrinsic.width}
      height={height ?? resolved.height ?? intrinsic.height}
      /* The hero is the LCP element — making the browser wait for layout before
         it starts fetching costs a full round trip. Everything else waits. */
      loading={priority ? "eager" : "lazy"}
      /* React 18 does not map the lowercase spelling: it warned on every page
         that used it and dropped the attribute, so the LCP image was fetched at
         default priority anyway. */
      fetchPriority={priority ? "high" : undefined}
      decoding={priority ? "sync" : "async"}
      className={className}
      onError={(event) => {
        if (!useFallback && fallbackDescriptor?.src && fallbackDescriptor.src !== resolved.src) {
          setUseFallback(true);
        }
        onError?.(event);
      }}
      {...rest}
    />
  );

  if (!mobile?.src) return image;
  return (
    <picture>
      <source media="(max-width: 767px)" srcSet={mobile.srcSet || mobile.src} sizes={mobile.srcSet ? sizes : undefined} />
      {image}
    </picture>
  );
}
